import type { Actor, StaffActor, TenantActor } from '../types/actor.js';
import { STAFF_RESOURCE_KEYS, type Level, type Resource, type StaffResource } from '../types/rbac.js';
import type { StaffRole, TenantUser } from '../types/user.js';
import { isTenantResource } from './rbac.js';

/** What each DinoCloud role may do outside impersonation. */
export const STAFF_MATRIX: Record<StaffRole, Record<StaffResource, Level>> = {
  superadmin: { tenants: 'full', entitle: 'full', staff: 'full', audit: 'full' },
  staff: { tenants: 'full', entitle: 'full', staff: 'read', audit: 'read' },
};

function isStaffResource(value: string): value is StaffResource {
  return (STAFF_RESOURCE_KEYS as readonly string[]).includes(value);
}

/**
 * The one permission check. Call it everywhere: nav, route and API.
 *
 * - A tenant actor reads its resolved levels (already intersected with entitlements).
 * - Staff impersonating a tenant is checked as the persona, never as themselves.
 * - Staff outside impersonation hold their role's staff matrix and full access to every
 *   tenant resource, which is how they administer any tenant's roles and groups.
 */
export function can(actor: Actor, resource: Resource): Level {
  if (actor.kind === 'tenant') {
    return isTenantResource(resource) ? actor.levels[resource] : 'none';
  }
  if (actor.impersonating) {
    return can(actor.impersonating.persona, resource);
  }
  if (isStaffResource(resource)) return STAFF_MATRIX[actor.role][resource];
  return 'full';
}

export function isStaff(actor: Actor): actor is StaffActor {
  return actor.kind === 'staff';
}

/** Staff acting as themselves, not as a tenant persona. */
export function isStaffSurface(actor: Actor): boolean {
  return actor.kind === 'staff' && !actor.impersonating;
}

/** The tenant a request is scoped to, if any. */
export function actorTenantId(actor: Actor): string | null {
  if (actor.kind === 'tenant') return actor.tenant.tenantId;
  return actor.impersonating?.persona.tenant.tenantId ?? null;
}

/** The persona the UI shows in the header. */
export function personaOf(actor: Actor): TenantActor | StaffActor {
  return actor.kind === 'staff' && actor.impersonating ? actor.impersonating.persona : actor;
}

/**
 * Who may modify a tenant user. Requires full access to management. The superadmin is
 * never modifiable this way: handoff is the only path, for everyone including staff.
 */
export function canManageUser(actor: Actor, target: Pick<TenantUser, 'isSuperadmin' | 'id'>): boolean {
  if (target.isSuperadmin) return false;
  return can(actor, 'management') === 'full';
}

/** Only the current superadmin can start a handoff of their own role. */
export function canRequestHandoff(actor: Actor): boolean {
  const persona = personaOf(actor);
  return persona.kind === 'tenant' && persona.isSuperadmin;
}
